// The sidebar WKT row prints each feature's geometry exactly as the layer delivered
// it (EPSG:4326). Map features live in EPSG:3857, and writing them back to 4326
// invents digits (71.3295807 -> 71.32958070000001); rounding them would be a guess
// too. So the loaders keep the as-received geometry here, outside the OL property
// bag so property iterators never see it.
const SOURCE_GEOMETRIES = new WeakMap();

// Keep `geometry` (GeoJSON object, or an untransformed 4326 OL geometry) as the
// source of `feature`. A derived feature (CaJ arc) gets its WFS relation's source;
// null means "no source geometry" and suppresses the row.
export function setSourceGeometry(feature, geometry) {
  SOURCE_GEOMETRIES.set(feature, geometry ?? null);
}

export function getSourceGeometry(feature) {
  return SOURCE_GEOMETRIES.get(feature);
}

// Adds source capture to a GeoJSON reader class (ol/format/GeoJSON in the app; passed
// in so this module stays dependency-free). The kept copy is the reader's own
// untransformed read, taken before the map feature is built and cloned, so it never
// shares arrays with the geometry the reader reprojects in place.
export function withSourceGeometry(GeoJSONClass) {
  return class extends GeoJSONClass {
    readFeatureFromObject(object, options) {
      const raw = object.type === "Feature" ? object.geometry : object;
      const source = raw ? this.readGeometryFromObject(raw)?.clone() : null;
      const feature = super.readFeatureFromObject(object, options);
      setSourceGeometry(feature, source);
      return feature;
    }
  };
}

// WKT of the feature's source geometry, or undefined when it has none. Features no
// loader registered (derived, drawn) also get none: never print a rendering.
export function sourceWkt(feature) {
  const geometry = SOURCE_GEOMETRIES.get(feature);
  if (!geometry) {
    return undefined;
  }
  try {
    return geometryToWkt(geometry);
  } catch (err) {
    console.warn("No WKT for feature", feature.getId?.(), err);
    return undefined;
  }
}

const WKT_TYPES = {
  Point: "POINT",
  LineString: "LINESTRING",
  Polygon: "POLYGON",
  MultiPoint: "MULTIPOINT",
  MultiLineString: "MULTILINESTRING",
  MultiPolygon: "MULTIPOLYGON",
  GeometryCollection: "GEOMETRYCOLLECTION",
};

// Nesting depth of a coordinate array per type (Point = a single position).
const DEPTH = {
  Point: 0,
  LineString: 1,
  MultiPoint: 1,
  Polygon: 2,
  MultiLineString: 2,
  MultiPolygon: 3,
};

const LAYOUT_SUFFIX = { XY: "", XYZ: " Z", XYM: " M", XYZM: " ZM" };

// OL geometries are read through their own accessors (layout included, as GeoJSON
// can't say XYM); GeoJSON objects as-is.
function toGeoJson(geometry) {
  if (typeof geometry.getType !== "function") {
    return geometry;
  }
  const type = geometry.getType();
  return type === "GeometryCollection"
    ? { type, geometries: geometry.getGeometries() }
    : {
        type,
        coordinates: geometry.getCoordinates(),
        layout: geometry.getLayout(),
      };
}

// Numbers are printed with String(), the shortest text that parses back to the same
// double: the WFS value, minus insignificant trailing zeros. Output format mirrors
// ol/format/WKT (`TYPE(…)`, `TYPE Z(…)`, `TYPE Z EMPTY`, no spaces after commas).
export function geometryToWkt(input) {
  const geometry = toGeoJson(input);
  const wktType = WKT_TYPES[geometry.type];
  if (!wktType) {
    throw new Error(`Unsupported geometry type: ${geometry.type}`);
  }
  if (geometry.type === "GeometryCollection") {
    const parts = geometry.geometries.map(geometryToWkt);
    return parts.length ? `${wktType}(${parts.join(",")})` : `${wktType} EMPTY`;
  }
  const depth = DEPTH[geometry.type];
  const multiPoint = geometry.type === "MultiPoint";
  const body = encode(geometry.coordinates, depth, multiPoint);
  const layout =
    LAYOUT_SUFFIX[geometry.layout ?? geoJsonLayout(geometry, depth)];
  return body ? `${wktType}${layout}(${body})` : `${wktType}${layout} EMPTY`;
}

// depth 0: "x y"; depth 1: "x y,x y"; deeper levels wrap each child in parens.
// MultiPoint (depth 1) wraps each point, matching ol/format/WKT.
function encode(coordinates, depth, multiPoint = false) {
  if (depth === 0) {
    return coordinates.join(" ");
  }
  if (depth === 1) {
    return coordinates
      .map((c) => (multiPoint ? `(${c.join(" ")})` : c.join(" ")))
      .join(",");
  }
  return coordinates.map((c) => `(${encode(c, depth - 1)})`).join(",");
}

// GeoJSON layout from the first position's ordinate count, as ol/format/GeoJSON reads it.
function geoJsonLayout(geometry, depth) {
  let position = geometry.coordinates;
  for (let i = 0; i < depth; i++) {
    position = position?.[0];
  }
  const ordinates = position?.length ?? 2;
  return ordinates >= 4 ? "XYZM" : ordinates === 3 ? "XYZ" : "XY";
}
